import { useEffect, useState } from 'react';
import type { RefObject } from 'react';
import { Button } from './Button';
import { StepDots } from './StepDots';

export interface CoachMarkStep {
  /** Resolves the live element to spotlight (brief §6: real UI, never screenshots). */
  target: () => Element | null;
  title: string;
  body: string;
  /** Cutout corner radius = target radius + 8 (defaults to 16). */
  radius?: number;
}

export interface CoachMarksLabels {
  /** t('ec2.tour.skip'). */
  skip: string;
  /** t('ec2.tour.next'). */
  next: string;
  /** t('ec2.tour.letsPlay') — last step's primary button. */
  done: string;
  /** t('ec2.tour.step', {current, total}) — SR progress. */
  step?: string;
}

export interface CoachMarksProps {
  open: boolean;
  steps: CoachMarkStep[];
  /** Controlled zero-based step index. */
  stepIndex: number;
  labels: CoachMarksLabels;
  onNext: () => void;
  onSkip: () => void;
  /** Fired by the last step's primary button (`Let's play`). */
  onDone: () => void;
  /** Set `inert` on the app content under the scrim while open (brief §6). */
  underlayRef?: RefObject<HTMLElement | null>;
  className?: string;
}

interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

const PAD = 8;
/** Room kept clear at the top/bottom (header, tab bar, caption card) when bringing a target into view. */
const VIEW_MARGIN = 96;

/**
 * Bring the step's target into view — a quest card further down the list, or
 * the balance at the top after the player scrolled. Only when it isn't already
 * comfortably visible, so fixed elements (the tab bar) never cause a jump, and
 * without animation under prefers-reduced-motion. The scroll listener below
 * re-measures, so the spotlight and caption follow it.
 */
function bringIntoView(el: Element) {
  const r = el.getBoundingClientRect();
  const vh = window.innerHeight;
  if (r.top >= VIEW_MARGIN && r.bottom <= vh - VIEW_MARGIN) return;
  const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  el.scrollIntoView?.({ block: 'center', behavior: reduced ? 'auto' : 'smooth' });
}

/**
 * First-run guided tour (brief §6): full-screen 72%-ink scrim with an SVG-mask
 * spotlight cutout over the live target; the lock-on brackets sit on the cutout
 * corners in --primary — the tour literally locks onto each element. Caption card
 * (max 280px) is placed above or below the cutout, whichever half has room; step
 * beats reuse StepDots; Skip is always visible.
 */
export function CoachMarks({
  open,
  steps,
  stepIndex,
  labels,
  onNext,
  onSkip,
  onDone,
  underlayRef,
  className = '',
}: CoachMarksProps) {
  const [rect, setRect] = useState<Rect | null>(null);
  const step = steps[stepIndex];
  const isLast = stepIndex === steps.length - 1;

  useEffect(() => {
    if (!open || !step) return;
    const measure = () => {
      const el = step.target();
      if (!el) {
        setRect(null);
        return;
      }
      const r = el.getBoundingClientRect();
      setRect({ x: r.x - PAD, y: r.y - PAD, width: r.width + PAD * 2, height: r.height + PAD * 2 });
    };
    const target = step.target();
    if (target) bringIntoView(target);
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [open, step, stepIndex]);

  useEffect(() => {
    const underlay = underlayRef?.current;
    if (!underlay) return;
    if (open) {
      underlay.setAttribute('inert', '');
      return () => underlay.removeAttribute('inert');
    }
    underlay.removeAttribute('inert');
  }, [open, underlayRef]);

  if (!open || !step) return null;

  const vw = typeof window !== 'undefined' ? window.innerWidth : 390;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 844;
  const r = rect ?? { x: vw / 2 - 60, y: vh / 2 - 60, width: 120, height: 120 };
  const radius = (step.radius ?? 8) + PAD;
  const cardBelow = r.y + r.height / 2 < vh / 2;

  const bracket = 22;
  const brackets = [
    `M ${r.x} ${r.y + bracket} V ${r.y + 6} Q ${r.x} ${r.y} ${r.x + 6} ${r.y} H ${r.x + bracket}`,
    `M ${r.x + r.width - bracket} ${r.y} H ${r.x + r.width - 6} Q ${r.x + r.width} ${r.y} ${r.x + r.width} ${r.y + 6} V ${r.y + bracket}`,
    `M ${r.x + r.width} ${r.y + r.height - bracket} V ${r.y + r.height - 6} Q ${r.x + r.width} ${r.y + r.height} ${r.x + r.width - 6} ${r.y + r.height} H ${r.x + r.width - bracket}`,
    `M ${r.x + bracket} ${r.y + r.height} H ${r.x + 6} Q ${r.x} ${r.y + r.height} ${r.x} ${r.y + r.height - 6} V ${r.y + r.height - bracket}`,
  ].join(' ');

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={step.title}
      data-testid="ec-coach-marks"
      className={`fixed inset-0 z-50 ${className}`}
    >
      <svg aria-hidden className="absolute inset-0 h-full w-full" data-testid="ec-spotlight">
        <defs>
          <mask id="ec-coach-cutout">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            <rect
              x={r.x}
              y={r.y}
              width={r.width}
              height={r.height}
              rx={radius}
              fill="black"
              className="transition-all duration-(--duration-slow)"
            />
          </mask>
        </defs>
        <rect x="0" y="0" width="100%" height="100%" fill="var(--scrim-tour)" mask="url(#ec-coach-cutout)" />
        <path
          d={brackets}
          fill="none"
          stroke="var(--primary)"
          strokeWidth="3"
          strokeLinecap="round"
          className="ec-bracket-snap transition-all duration-(--duration-slow)"
        />
      </svg>

      <div
        data-testid="ec-coach-card"
        className="absolute left-1/2 w-[280px] -translate-x-1/2 rounded-(--radius-card) bg-surface-raised p-4 shadow-xl"
        style={cardBelow ? { top: r.y + r.height + 16 } : { bottom: vh - r.y + 16 }}
      >
        <h2 className="text-[17px] font-semibold text-fg">{step.title}</h2>
        <p className="mt-1 text-sm text-fg-secondary">{step.body}</p>
        <div className="mt-3 flex items-center justify-between gap-3">
          <StepDots count={steps.length} active={stepIndex} label={labels.step} />
        </div>
        <div className="mt-3 flex items-center justify-between gap-3">
          <Button variant="ghost" onClick={onSkip} className="px-4 py-2 text-sm" data-testid="ec-coach-skip">
            {labels.skip}
          </Button>
          <Button
            variant="primary"
            onClick={isLast ? onDone : onNext}
            className="px-4 py-2 text-sm"
            data-testid="ec-coach-next"
          >
            {isLast ? labels.done : labels.next}
          </Button>
        </div>
      </div>
    </div>
  );
}
